import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  Divider,
  IconButton,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react'
import { SearchIcon, CopyIcon } from '@chakra-ui/icons'
import dayjs from 'dayjs'
import {
  getWithdrawalList,
  cancelWithdrawalAdmin,
  approveWithdrawalAdmin
} from '../../services/apiService'

// 관리자 id 사용
import { useRecoilState } from 'recoil'
import { authState } from '../../atoms/auth'

type WithdrawalItem = {
  id: number
  userId: number
  type: 'WD_WAIT' | 'WD_OK' | 'WD_NO' | 'WD_RB'
  withdrawalAmount: string | null
  fee: string | null
  network: string | null
  walletAddress: string | null
  beforeUsdtBalance: string | null
  afterUsdtBalance: string | null
  txid: string | null
  feeTxid: string | null
  createdAt: string | null
  account?: string
  username?: string
  phone?: string
}

type StatusFilter = 'ALL' | 'WAIT' | 'OK' | 'NO' | 'RB'

const STATUS_META: Record<
  WithdrawalItem['type'],
  { label: string; color: string }
> = {
  WD_WAIT: { label: '출금요청', color: 'yellow' },
  WD_OK: { label: '출금완료', color: 'green' },
  WD_NO: { label: '출금취소', color: 'red' },
  WD_RB: { label: '요청반환', color: 'cyan' }
}

const Withdrawal = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [auth] = useRecoilState(authState)

  // 검색/필터/페이지네이션 상태
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<StatusFilter>('ALL')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<WithdrawalItem[]>([])
  const [loading, setLoading] = useState(false)

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  // 말미 0 제거 + 천단위 콤마
  const formatAmt = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const [int, frac = ''] = String(v).split('.')
    const fracTrimmed = frac.replace(/0+$/, '')
    const intComma = Number(int).toLocaleString()
    return fracTrimmed ? `${intComma}.${fracTrimmed}` : intComma
  }

  // 액션 상태
  const [selected, setSelected] = useState<WithdrawalItem | null>(null)

  // 승인 컨펌 모달
  const approveModal = useDisclosure()
  const [approveLoading, setApproveLoading] = useState(false)

  // 거절(txid 입력) 모달
  const rejectModal = useDisclosure()
  const [rejectLoading, setRejectLoading] = useState(false)
  const [rejectTxid, setRejectTxid] = useState('')

  // 공통 포맷
  const formatDate = (v?: string | null) => {
    if (!v) return '-'
    const d = dayjs(v)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : v
  }

  const formatUSDT = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const [int, frac = ''] = String(v).split('.')
    const fracTrimmed = frac.replace(/0+$/, '')
    const intComma = Number(int).toLocaleString()
    return fracTrimmed ? `${intComma}.${fracTrimmed}` : intComma
  }

  // 복사
  const copy = async (text?: string | null, label = '복사') => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: `${label} 완료`, status: 'success', duration: 1200 })
    } catch {
      toast({ title: `${label} 실패`, status: 'error', duration: 1500 })
    }
  }

  // 리스트 로드
  const fetchList = async (
    override?: Partial<{
      page: number
      pageSize: number
      keyword: string
      status: StatusFilter
    }>
  ) => {
    try {
      setLoading(true)
      const params = {
        page,
        pageSize,
        keyword,
        status: status === 'ALL' ? undefined : status,
        ...override
      }
      const res = await getWithdrawalList(params)
      const payload = res?.data?.data
      setItems(payload?.items ?? [])
      setTotal(payload?.total ?? 0)
      if (typeof payload?.page === 'number') setPage(payload.page)
      if (typeof payload?.pageSize === 'number') setPageSize(payload.pageSize)
    } catch (e: any) {
      toast({
        title: '출금 목록 조회 실패',
        description: e?.message ?? '네트워크 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status])

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1 })
  }

  // ===== 액션: 승인 =====
  const openApprove = (w: WithdrawalItem) => {
    setSelected(w)
    approveModal.onOpen()
  }

  // 승인
  const [approveTxid, setApproveTxid] = useState('')

  const handleApprove = async () => {
    if (!selected) return
    if (!auth?.id) {
      toast({ title: '관리자 정보가 없습니다.', status: 'error' })
      return
    }
    const txid = (approveTxid || '').trim()
    if (!txid) {
      toast({ title: 'TXID를 입력하세요.', status: 'warning' })
      return
    }
    try {
      setApproveLoading(true)
      await approveWithdrawalAdmin({
        adminId: auth.id,
        historyId: String(selected.id),
        txid
      })
      toast({ title: '출금 승인 완료', status: 'success' })
      approveModal.onClose()
      setApproveTxid('')
      setSelected(null)
      fetchList()
    } catch (e: any) {
      toast({
        title: '출금 승인 실패',
        description: e?.response?.data?.message || e?.message,
        status: 'error'
      })
    } finally {
      setApproveLoading(false)
    }
  }

  // ===== 액션: 거절 (txid 입력) =====
  const openReject = (w: WithdrawalItem) => {
    setSelected(w)
    setRejectTxid('') // 기본값 비움
    rejectModal.onOpen()
  }

  // 거절
  const handleReject = async () => {
    if (!selected) return
    if (!auth?.id) {
      toast({ title: '관리자 정보가 없습니다.', status: 'error' })
      return
    }
    try {
      setRejectLoading(true)
      await cancelWithdrawalAdmin({
        adminId: auth.id,
        historyId: String(selected.id)
      })
      toast({ title: '출금 거절 완료', status: 'success' })
      rejectModal.onClose()
      setSelected(null)
      fetchList()
    } catch (e: any) {
      toast({
        title: '출금 거절 실패',
        description: e?.response?.data?.message || e?.message,
        status: 'error'
      })
    } finally {
      setRejectLoading(false)
    }
  }

  // 모바일 카드
  const MobileCard = ({ w }: { w: WithdrawalItem }) => {
    const meta = STATUS_META[w.type]
    const isWait = w.type === 'WD_WAIT'
    return (
      <Box borderWidth="1px" borderRadius="lg" p={4}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontWeight="bold">{w.username || '-'}</Text>
          <Badge colorScheme={meta.color as any} variant="subtle">
            {meta.label}
          </Badge>
        </Flex>
        <Text fontSize="sm" color="gray.400">
          @{w.account || '-'}
        </Text>
        <Divider my={3} />

        <Stack spacing={1} fontSize="sm">
          <Flex justify="space-between">
            <Text color="gray.500">출금금액</Text>
            <Text fontWeight="semibold">{formatUSDT(w.withdrawalAmount)}</Text>
          </Flex>
          <Flex justify="space-between">
            <Text color="gray.500">수수료</Text>
            <Text>{w.fee ?? '-'}</Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text color="gray.500">네트워크</Text>
            <Badge
              colorScheme={
                (w.network || '').toUpperCase() === 'TRC' ? 'orange' : 'blue'
              }
              variant="subtle"
            >
              {w.network || '-'}
            </Badge>
          </Flex>
          <Flex justify="space-between" align="center" gap={2}>
            <Text color="gray.500">지갑주소</Text>
            <HStack spacing={1} maxW="65%">
              <Tooltip label={w.walletAddress || ''} hasArrow>
                <Text noOfLines={1}>{w.walletAddress || '-'}</Text>
              </Tooltip>
              {w.walletAddress && (
                <IconButton
                  aria-label="copy address"
                  size="xs"
                  variant="ghost"
                  icon={<CopyIcon />}
                  onClick={() => copy(w.walletAddress, '지갑주소')}
                />
              )}
            </HStack>
          </Flex>
          <Flex justify="space-between" align="center" gap={2}>
            <Text color="gray.500">출금 TXID</Text>
            <HStack spacing={1} maxW="65%">
              <Tooltip label={w.txid || ''} hasArrow>
                <Text noOfLines={1}>{w.txid || '-'}</Text>
              </Tooltip>
              {w.txid && (
                <IconButton
                  aria-label="copy txid"
                  size="xs"
                  variant="ghost"
                  icon={<CopyIcon />}
                  onClick={() => copy(w.txid, '출금 TXID')}
                />
              )}
            </HStack>
          </Flex>
          <Flex justify="space-between" align="center" gap={2}>
            <Text color="gray.500">수수료 TXID</Text>
            <HStack spacing={1} maxW="65%">
              <Tooltip label={w.feeTxid || ''} hasArrow>
                <Text noOfLines={1}>{w.feeTxid || '-'}</Text>
              </Tooltip>
              {w.feeTxid && (
                <IconButton
                  aria-label="copy fee txid"
                  size="xs"
                  variant="ghost"
                  icon={<CopyIcon />}
                  onClick={() => copy(w.feeTxid, '수수료 TXID')}
                />
              )}
            </HStack>
          </Flex>
          <Flex justify="space-between">
            <Text color="gray.500">연락처</Text>
            <Text>{w.phone || '-'}</Text>
          </Flex>
          <Flex justify="space-between">
            <Text color="gray.500">요청일시</Text>
            <Text>{formatDate(w.createdAt)}</Text>
          </Flex>
        </Stack>

        {/* WD_WAIT 액션 */}
        {isWait && (
          <HStack mt={3} spacing={2}>
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => openApprove(w)}
            >
              승인
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={() => openReject(w)}
            >
              거절
            </Button>
          </HStack>
        )}
      </Box>
    )
  }

  return (
    <DefaultLayout>
      <AdminNav />

      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 타이틀/검색/필터 */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={3}
          gap={3}
        >
          <Heading as="h2" size="md" color="white">
            출금 관리
          </Heading>

          <HStack spacing={3} align="stretch">
            <InputGroup width={{ base: '100%', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} />
              </InputLeftElement>
              <Input
                placeholder="아이디/이름/연락처/TXID"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              />
            </InputGroup>
            <Button onClick={onSearch} colorScheme="orange" bgGradient="linear(to-r, brand.500, blue.500)" color="white" _hover={{ bgGradient: "linear(to-r, brand.400, blue.400)" }} minW={{ base: 'auto', md: '70px' }}>
              검색
            </Button>

            <Select
              width={{ base: '120px', md: '140px' }}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                setPage(1)
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}개씩
                </option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* 상태 필터 */}
        <HStack spacing={2} mb={3} wrap="wrap">
          <ButtonGroup isAttached size="sm" variant="outline">
            {(
              [
                { key: 'ALL', label: '전체' },
                { key: 'WAIT', label: '요청' },
                { key: 'OK', label: '완료' },
                { key: 'NO', label: '취소' },
                { key: 'RB', label: '반환' }
              ] as { key: StatusFilter; label: string }[]
            ).map((opt) => (
              <Button
                key={opt.key}
                colorScheme={status === opt.key ? 'blue' : undefined}
                onClick={() => {
                  setStatus(opt.key)
                  setPage(1)
                }}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </HStack>

        {/* 총 건수/페이지 */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text color="gray.400" fontSize="sm">
            총 <b>{total.toLocaleString()}</b>건
          </Text>
          <Text color="gray.500" fontSize="sm">
            페이지 {page} / {totalPages}
          </Text>
        </Flex>

        {/* 리스트 */}
        {loading ? (
          <Flex align="center" justify="center" py={8} gap={2}>
            <Spinner />
            <Text>불러오는 중…</Text>
          </Flex>
        ) : items.length === 0 ? (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.500">데이터가 없습니다.</Text>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {items.map((w) => (
              <MobileCard key={w.id} w={w} />
            ))}
          </Stack>
        ) : (
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table size="sm">
              <Thead bg="dark.card">
                <Tr>
                  <Th>번호</Th>
                  <Th>아이디</Th>
                  <Th>이름</Th>
                  <Th>연락처</Th>
                  <Th>상태</Th>
                  <Th isNumeric>출금금액</Th>
                  <Th>수수료</Th>
                  <Th>잔액 (전 → 후)</Th>
                  <Th>네트워크</Th>
                  <Th>지갑주소</Th>
                  <Th>출금 TXID</Th>
                  <Th>요청일시</Th>
                  <Th>액션</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((w) => {
                  const meta = STATUS_META[w.type]
                  const isWait = w.type === 'WD_WAIT'
                  return (
                    <Tr key={w.id}>
                      <Td>{w.id}</Td>
                      <Td>{w.account || '-'}</Td>
                      <Td>{w.username || '-'}</Td>
                      <Td>{w.phone || '-'}</Td>
                      <Td>
                        <Badge colorScheme={meta.color as any} variant="subtle">
                          {meta.label}
                        </Badge>
                      </Td>
                      <Td isNumeric>{formatUSDT(w.withdrawalAmount)}</Td>
                      <Td>{w.fee ?? '-'}</Td>
                      <Td>
                        {formatAmt(w.beforeUsdtBalance)} →{' '}
                        {formatAmt(w.afterUsdtBalance)}
                      </Td>
                      <Td>
                        <Badge
                          variant="subtle"
                          colorScheme={
                            (w.network || '').toUpperCase() === 'TRC'
                              ? 'orange'
                              : 'blue'
                          }
                        >
                          {w.network || '-'}
                        </Badge>
                      </Td>
                      <Td maxW="260px">
                        <HStack spacing={1}>
                          <Tooltip label={w.walletAddress || ''} hasArrow>
                            <Text noOfLines={1}>{w.walletAddress || '-'}</Text>
                          </Tooltip>
                          {w.walletAddress && (
                            <IconButton
                              aria-label="copy address"
                              size="xs"
                              variant="ghost"
                              icon={<CopyIcon />}
                              onClick={() => copy(w.walletAddress, '지갑주소')}
                            />
                          )}
                        </HStack>
                      </Td>
                      <Td maxW="260px">
                        <HStack spacing={1}>
                          <Tooltip label={w.txid || ''} hasArrow>
                            <Text noOfLines={1}>{w.txid || '-'}</Text>
                          </Tooltip>
                          {w.txid && (
                            <IconButton
                              aria-label="copy txid"
                              size="xs"
                              variant="ghost"
                              icon={<CopyIcon />}
                              onClick={() => copy(w.txid, '출금 TXID')}
                            />
                          )}
                        </HStack>
                      </Td>
                      <Td>{formatDate(w.createdAt)}</Td>
                      <Td>
                        {isWait ? (
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              colorScheme="green"
                              onClick={() => openApprove(w)}
                            >
                              승인
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => openReject(w)}
                            >
                              거절
                            </Button>
                          </HStack>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            -
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* 페이지네이션 */}
        <Flex align="center" justify="center" gap={2} mt={4}>
          <Button
            onClick={() => setPage(1)}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            처음
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            이전
          </Button>
          <Text fontSize="sm">
            {page} / {totalPages}
          </Text>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            다음
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            마지막
          </Button>
        </Flex>
      </Box>

      {/* 승인 모달 (txid 입력 필요) */}
      <Modal
        isOpen={approveModal.isOpen}
        onClose={() => {
          if (!approveLoading) approveModal.onClose()
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>출금 승인</ModalHeader>
          <ModalBody>
            <Stack spacing={2} fontSize="sm" mb={3}>
              <Text>
                사용자: <b>{selected?.username || '-'}</b> (@
                {selected?.account || '-'})
              </Text>
              <Text>
                요청 이력번호: <b>{selected?.id ?? '-'}</b>
              </Text>
              <Text>
                출금금액: <b>{formatUSDT(selected?.withdrawalAmount)}</b>
              </Text>
            </Stack>
            <Text fontWeight="semibold" mb={1}>
              TXID
            </Text>
            <Input
              placeholder="승인 시 기록할 TXID 입력"
              value={approveTxid}
              onChange={(e) => setApproveTxid(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={approveModal.onClose}
              isDisabled={approveLoading}
            >
              취소
            </Button>
            <Button
              colorScheme="green"
              onClick={handleApprove}
              isLoading={approveLoading}
            >
              승인
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 거절 모달 (txid 불필요, 단순 컨펌) */}
      <Modal
        isOpen={rejectModal.isOpen}
        onClose={() => {
          if (!rejectLoading) rejectModal.onClose()
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>출금 거절</ModalHeader>
          <ModalBody>
            <Stack spacing={2} fontSize="sm">
              <Text>
                사용자: <b>{selected?.username || '-'}</b> (@
                {selected?.account || '-'})
              </Text>
              <Text>
                요청 이력번호: <b>{selected?.id ?? '-'}</b>
              </Text>
              <Text>
                출금금액: <b>{formatUSDT(selected?.withdrawalAmount)}</b>
              </Text>
            </Stack>
            <Text mt={3} fontSize="sm" color="gray.400">
              출금을 거절하시겠습니까?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={rejectModal.onClose}
              isDisabled={rejectLoading}
            >
              취소
            </Button>
            <Button
              colorScheme="red"
              variant="solid"
              onClick={handleReject}
              isLoading={rejectLoading}
            >
              거절
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default Withdrawal
